import { Autocomplete, Paper, TextField, Typography } from "@mui/material";
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";

import { useDropdownSourceContext } from "@ryde/redux";
import { IDropdownItem, weekDays } from "@ryde/shared";

import { MuiButton } from "../../../controls";
import { getDropdownValuesLabels } from "../dropdowns";
import {
  IShiftDayProps,
  TShiftTemplateFieldName,
} from "./shift-cycle.template.models";

export const ShiftDay: FC<IShiftDayProps> = ({
  value,
  index,
  update,
  remove,
  isRemoveEnabled,
}) => {
  const { t } = useTranslation();
  const [regular, setRegular] = useState<number[]>(value.regular || []);
  const [holidayEve, setHolidayEve] = useState<number[]>(
    value.holidayEve || [],
  );
  const [holiday, setHoliday] = useState<number[]>(value.holiday || []);

  const { getDropdownItems } = useDropdownSourceContext();
  const [dropDownItems, setDropDownItems] = useState<
    IDropdownItem[] | undefined
  >(undefined);

  const [optionValues, mapOptionLabel] = useMemo(
    () => getDropdownValuesLabels({ dropDownItems }),
    [dropDownItems],
  );
  const [, startTransition] = useTransition();

  const getOptionLabel = useCallback(
    (val: unknown) => {
      return mapOptionLabel.get(val as string | number) || "";
    },
    [mapOptionLabel],
  );

  useEffect(() => {
    const getItems = async (): Promise<void> => {
      const items = await getDropdownItems?.("shift");
      setDropDownItems(items || []);
    };

    startTransition(() => {
      getItems();
    });
  }, [getDropdownItems]);

  const handleChange = (
    values: readonly number[],
    fieldName: TShiftTemplateFieldName,
  ): void => {
    const fieldSetters = {
      regular: setRegular,
      holidayEve: setHolidayEve,
      holiday: setHoliday,
    };

    fieldSetters[fieldName]([...values]);
    update(index, {
      regular: [...regular],
      holidayEve: [...holidayEve],
      holiday: [...holiday],
      [fieldName]: values,
    });
  };

  const handleRemove = (): void => {
    remove(index);
  };

  return (
    <Paper
      sx={{ padding: "20px", marginBottom: "20px" }}
      data-e2e-template="shift-cycle-template"
    >
      <Typography variant="h6">
        {isRemoveEnabled
          ? `${t("general.calendar.DAY")} ${index + 1}`
          : `${t(`general.calendar.${weekDays[index].toUpperCase()}`)}`}
      </Typography>
      <Autocomplete
        multiple
        data-e2e="shift-template-regular"
        options={optionValues ?? []}
        getOptionLabel={getOptionLabel}
        onChange={(event, values) =>
          handleChange(values as number[], "regular")
        }
        value={regular}
        renderInput={params => (
          <TextField
            {...params}
            label={t("shiftTemplate.regular")}
            margin="normal"
            variant="outlined"
            data-e2e="regular-day-input"
            required
          />
        )}
      />
      <Autocomplete
        multiple
        data-e2e="shift-template-holiday-eve"
        options={optionValues ?? []}
        getOptionLabel={getOptionLabel}
        onChange={(event, values) =>
          handleChange(values as number[], "holidayEve")
        }
        value={holidayEve}
        renderInput={params => (
          <TextField
            {...params}
            label={`${t("shiftTemplate.holidayEve")} (${t("shiftTemplate.optional")})`}
            margin="normal"
            variant="outlined"
            data-e2e="holiday-eve-input"
          />
        )}
      />
      <Autocomplete
        multiple
        data-e2e="shift-template-holiday"
        options={optionValues ?? []}
        getOptionLabel={getOptionLabel}
        onChange={(event, values) =>
          handleChange(values as number[], "holiday")
        }
        value={holiday}
        renderInput={params => (
          <TextField
            {...params}
            label={`${t("shiftTemplate.holiday")} (${t("shiftTemplate.optional")})`}
            margin="normal"
            variant="outlined"
            data-e2e="holiday-input"
          />
        )}
      />
      {isRemoveEnabled && (
        <MuiButton
          onClick={handleRemove}
          muiStyle="error"
          muiVariant="outlined"
          e2eData="button-remove-day"
        >
          {t("shiftTemplate.removeDay")}
        </MuiButton>
      )}
    </Paper>
  );
};
